import { Component, EventEmitter, Input, Output, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { VetChatSummaryDto, Claim } from '../../models';
import { DataStorageService } from '../../services/data-storage.service';
import { TranslateService } from '@ngx-translate/core';

interface DetailItem {
  id: string;
  title: string;
  type: 'chat' | 'claim';
  petName: string;
  originalItem: VetChatSummaryDto | Claim;
}

@Component({
  selector: 'cpf-chat-claim-details',
  templateUrl: './chat-claim-details.component.html',
  styleUrls: ['./chat-claim-details.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ChatClaimDetailsComponent implements OnInit {
  @Input() item?: DetailItem;
  @Output() backClicked = new EventEmitter<void>();

  public detailItem?: DetailItem;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private dataStorageService: DataStorageService,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    // If item is provided as input, use it
    if (this.item) {
      this.detailItem = this.item;
      return;
    }

    // Otherwise, get data from route parameters
    const type = this.route.snapshot.paramMap.get('type') as 'chat' | 'claim';
    const id = this.route.snapshot.paramMap.get('id');

    if (type && id) {
      this.loadItemFromRoute(type, id);
    }
  }

  private loadItemFromRoute(type: 'chat' | 'claim', id: string) {
    const petData = this.dataStorageService.selectedData;
    let originalItem: VetChatSummaryDto | Claim | undefined;
    let title = '';

    if (type === 'chat') {
      const chatSummaries = this.dataStorageService.chatSummaries;
      originalItem = chatSummaries.find(chat => chat.id === id);
      title = (originalItem as VetChatSummaryDto)?.medical_category || this.translate.instant('chat-claim-details.fallbacks.general-consultation');
    } else {
      const claims = this.dataStorageService.getAllData.map(d=>d.claims || []).flat() || [];
      originalItem = claims.find(claim => claim.identifier === id);
      title = (originalItem as Claim)?.identifier || '';
    }

    if (originalItem) {
      this.detailItem = {
        id,
        title,
        type,
        petName: petData?.pet_name || this.translate.instant('chat-claim-details.title.unknown-pet'),
        originalItem
      };
    }
  }

  navigateBack(): void {
    if (this.backClicked.observed) {
      this.backClicked.emit();
      return;
    }
    this.router.navigate(['calingo', 'telemedicine', 'chat-history']);
  }

  getStatusText(): string {
    return this.detailItem?.type === 'chat'
      ? this.translate.instant('chat-claim-details.status.online-vet')
      : this.translate.instant('chat-claim-details.status.vet-visit');
  }

  getTitle(): string {
    const typeKey = this.detailItem?.type === 'chat'
      ? 'chat-claim-details.title.online-consultation'
      : 'chat-claim-details.title.vet-visit';
    const type = this.translate.instant(typeKey);
    return `${type} ${this.detailItem?.petName}`;
  }

  getChatData(): VetChatSummaryDto | null {
    return this.detailItem?.type === 'chat' ? this.detailItem.originalItem as VetChatSummaryDto : null;
  }

  getClaimData(): Claim | null {
    return this.detailItem?.type === 'claim' ? this.detailItem.originalItem as Claim : null;
  }

  getSymptomsText(): string {
    const chatData = this.getChatData();
    if (chatData && chatData.symptoms && chatData.symptoms.length > 0) {
      return chatData.symptoms.join(', ');
    }
    return this.translate.instant('chat-claim-details.fallbacks.no-symptoms');
  }
}
